import { FC, useContext, useEffect } from 'react';
import PasswordInput from '../../components/PasswordInput/PasswordInput';
import PasswordValidator from '../../components/PasswordValidator/PasswordValidator';
import { Redirect } from 'react-router-dom';
import { ERoutes } from '../../util/constants';
import ResetPasswordContext, {
    ResetPasswordProvider,
    EResetPasswordUiState,
} from '../../contexts/ResetPasswordContext';
import API from '../../api/api';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Icon, Stack, useTheme } from '@mui/material';
import GlobalContext from '../../contexts/GlobalContext';
import ScreenHeader from '../../components/ScreenHeader/ScreenHeader';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';

const ResetPasswordSuccess: FC = () => {
    const { language, fullLoginLink } = useContext(GlobalContext);
    const theme = useTheme();

    const handleLogin = () => {
        fullLoginLink ? window.location.assign(fullLoginLink) : window.location.assign(ERoutes.login);
    };

    return (
        <Stack>
            <Box sx={{ textAlign: { lg: 'center' } }}>
                <Icon sx={{ color: `${theme.palette.primary.main}`, fontSize: 50 }}>check_circle</Icon>
            </Box>
            <ScreenHeader
                title={language.resetPassword.success.header}
                desc={language.resetPassword.success.subheader}
            />
            <Button
                component={'button'}
                variant="contained"
                size="large"
                onClick={handleLogin}
                sx={{ my: 2 }}
                fullWidth
            >
                {language.resetPassword.success.cta}
            </Button>
        </Stack>
    );
};

const ResetPasswordExpired: FC = () => {
    return <Redirect push to={{ pathname: ERoutes.forgotPassword, state: { linkExpired: true } }} />;
};

const ResetPasswordForm: FC = () => {
    const { language, getFullLoginLinkFromLocalStorage } = useContext(GlobalContext);
    const { password, setPassword, disableSubmit, resetPasswordUiState, submitResetPasswordForm } =
        useContext(ResetPasswordContext);

    const handleSubmitResetPassword = (event) => {
        event.preventDefault();
        API.post.trackEvent({ event: 'Sign In || Consumer Update Password | Button - Update password' });
        submitResetPasswordForm();
    };

    useEffect(() => {
        API.post.trackEvent({ event: 'Sign In || Consumer Update Password ' });
        getFullLoginLinkFromLocalStorage();
    }, []);

    return (
        <Stack>
            <ScreenHeader title={language.resetPassword.header} />
            {resetPasswordUiState === EResetPasswordUiState.error && (
                <Alert severity="error" sx={{ my: 2 }}>
                    {language.validation.genericError}
                </Alert>
            )}
            <form>
                <PasswordInput
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    label={language.resetPassword.newPasswordLabel}
                    autoFocus={true}
                    id="password"
                    size="small"
                />
                <Box sx={{ my: 2 }}>
                    <PasswordValidator password={password} />
                </Box>
                <LoadingButton
                    variant="contained"
                    type="submit"
                    disabled={disableSubmit || resetPasswordUiState === EResetPasswordUiState.pending}
                    loading={resetPasswordUiState === EResetPasswordUiState.pending}
                    onClick={handleSubmitResetPassword}
                    size="large"
                    fullWidth
                >
                    {language.resetPassword.submitPasswordCTA}
                </LoadingButton>
            </form>
        </Stack>
    );
};

const ResetPasswordUi: FC = () => {
    const { resetPasswordUiState } = useContext(ResetPasswordContext);
    let resetPasswordUi: React.ReactElement;
    switch (resetPasswordUiState) {
        case EResetPasswordUiState.default:
        case EResetPasswordUiState.pending:
        case EResetPasswordUiState.error:
            resetPasswordUi = <ResetPasswordForm />;
            break;
        case EResetPasswordUiState.success:
            resetPasswordUi = <ResetPasswordSuccess />;
            break;
        case EResetPasswordUiState.expired:
            resetPasswordUi = <ResetPasswordExpired />;
            break;
        default:
            throw new Error('Reset Password State set incorrectly');
    }
    return resetPasswordUi;
};

const ResetPasswordScreen: FC = () => {
    return (
        <ResetPasswordProvider>
            <PageLayoutV2>
                <ResetPasswordUi />
            </PageLayoutV2>
        </ResetPasswordProvider>
    );
};

export default ResetPasswordScreen;
